import { ClerkProvider, ClerkLoaded, useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';
import type { UserRole } from '@familychef/shared';
import { createContext, useContext, useEffect, useMemo, useCallback, type ReactNode } from 'react';

import { setFunctionsTokenGetter } from '@/lib/admin-api';
import { logger } from '@/lib/sentry';
import { setClerkTokenGetter } from '@/lib/supabase';

const clerkPublishableKey = import.meta.env['VITE_CLERK_PUBLISHABLE_KEY'] as string;

interface AuthContextValue {
  userId: string | null;
  email: string | null;
  userRole: UserRole;
  isAdmin: boolean;
  isLoading: boolean;
  isSignedIn: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function AuthContextProvider({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn, signOut: clerkSignOut, getToken } = useClerkAuth();
  const { user } = useUser();

  const userId = user?.externalId ?? user?.id ?? null;
  const email = user?.primaryEmailAddress?.emailAddress ?? null;
  const userRole = (user?.publicMetadata?.role as UserRole) ?? 'user';
  const isAdmin = userRole === 'admin';

  // Register Clerk session token getter on the Supabase client.
  // No template needed — Supabase verifies via JWKS (Third-Party Auth).
  const clerkGetToken = useCallback(async () => getToken(), [getToken]);

  useEffect(() => {
    setClerkTokenGetter(clerkGetToken);
    setFunctionsTokenGetter(clerkGetToken);
  }, [clerkGetToken]);

  const signOut = useCallback(async () => {
    await clerkSignOut();
    logger.setUser(null);
  }, [clerkSignOut]);

  const value = useMemo(
    () => ({
      userId,
      email,
      userRole,
      isAdmin,
      isLoading: !isLoaded,
      isSignedIn: !!isSignedIn,
      signOut,
    }),
    [userId, email, userRole, isAdmin, isLoaded, isSignedIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  return (
    <ClerkProvider publishableKey={clerkPublishableKey}>
      <ClerkLoaded>
        <AuthContextProvider>{children}</AuthContextProvider>
      </ClerkLoaded>
    </ClerkProvider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
